import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BLOG } from "@/constants/testIds";
import { renderMarkdown } from "@/lib/markdown";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return "";
    }
};

const renderBody = (body) => renderMarkdown(body);

const ensureMetaTag = (name) => {
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    return tag;
};

const ensureCanonical = () => {
    let tag = document.querySelector('link[rel="canonical"]');
    if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
    }
    return tag;
};

const upsertJsonLd = (id, data) => {
    let tag = document.getElementById(id);
    if (!tag) {
        tag = document.createElement("script");
        tag.type = "application/ld+json";
        tag.id = id;
        document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(data);
};

export default function BlogArticle() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);
        setArticle(null);
        (async () => {
            try {
                const res = await axios.get(`${API}/blog/${slug}`);
                if (mounted) setArticle(res.data);
            } catch (e) {
                if (mounted) {
                    setError(
                        e?.response?.status === 404
                            ? "We couldn't find that article."
                            : "Could not load the article right now."
                    );
                }
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => {
            mounted = false;
        };
    }, [slug]);

    // SEO/AEO/GEO: title, meta description, canonical, Article schema, FAQ schema
    useEffect(() => {
        if (!article) return;
        const prevTitle = document.title;
        const prevLang = document.documentElement.getAttribute("lang");
        const url = `${window.location.origin}/blog/${article.slug}`;

        if (article.lang) document.documentElement.setAttribute("lang", article.lang);
        document.title = article.meta_title || `${article.title} — Adx Fusion`;
        ensureMetaTag("description").setAttribute(
            "content",
            article.meta_description || article.summary || article.excerpt || ""
        );
        ensureCanonical().setAttribute("href", url);

        upsertJsonLd("ld-article", {
            "@context": "https://schema.org",
            "@type": "Article",
            inLanguage: article.lang || "en",
            headline: article.title,
            description: article.meta_description || article.summary || article.excerpt,
            image: article.cover_image ? [article.cover_image] : undefined,
            datePublished: article.published_at,
            dateModified: article.published_at,
            author: { "@type": "Organization", name: "Adx Fusion" },
            publisher: {
                "@type": "Organization",
                name: "Adx Fusion",
                url: window.location.origin,
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            articleSection: article.category,
            url,
        });

        if (article.faqs && article.faqs.length > 0) {
            upsertJsonLd("ld-faq", {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                inLanguage: article.lang || "en",
                mainEntity: article.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.question,
                    acceptedAnswer: { "@type": "Answer", text: f.answer },
                })),
            });
        } else {
            const faqTag = document.getElementById("ld-faq");
            if (faqTag) faqTag.remove();
        }

        return () => {
            document.title = prevTitle;
            if (prevLang) document.documentElement.setAttribute("lang", prevLang);
            else document.documentElement.removeAttribute("lang");
            const a = document.getElementById("ld-article");
            const f = document.getElementById("ld-faq");
            if (a) a.remove();
            if (f) f.remove();
        };
    }, [article]);

    return (
        <div data-testid={BLOG.article}>
            <section className="container-x pt-16 pb-8">
                <Link
                    to="/blog"
                    data-testid={BLOG.backLink}
                    className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-primary hover:text-brand-mist transition-colors"
                >
                    <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                    Back to blog
                </Link>
            </section>

            {loading ? (
                <section className="container-x pb-24">
                    <div className="flex items-center gap-3 text-brand-primary/70">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span className="text-sm uppercase tracking-widest">Loading article</span>
                    </div>
                </section>
            ) : error ? (
                <section className="container-x pb-32">
                    <p className="text-brand-frost/75 mb-8">{error}</p>
                    <Link to="/blog" className="btn-secondary">
                        Back to blog
                    </Link>
                </section>
            ) : article ? (
                <>
                    {/* HEADER */}
                    <section className="container-x pb-10">
                        <Reveal>
                            <div className="flex flex-wrap items-center gap-4 mb-8">
                                <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-brand-primary border border-brand-primary/40">
                                    {article.category}
                                </span>
                                <span className="text-xs uppercase tracking-[0.2em] text-brand-primary/70">
                                    {formatDate(article.published_at)}
                                </span>
                                <span className="text-xs uppercase tracking-[0.2em] text-brand-primary/50">
                                    {article.read_minutes} min read
                                </span>
                            </div>
                            <h1
                                className="font-display leading-[0.98] tracking-tight max-w-[1200px] text-brand-mist overflow-visible pr-2"
                                style={{ fontSize: "clamp(2rem, 5.5vw, 4.5rem)" }}
                            >
                                {article.title}
                            </h1>
                        </Reveal>

                        {/* Direct-answer summary, immediately under H1 */}
                        {article.summary ? (
                            <Reveal delay={120} className="mt-10 max-w-3xl">
                                <div className="border-l-2 border-brand-primary pl-6 py-2">
                                    <p className="overline mb-3">In short</p>
                                    <p className="text-xl text-brand-frost leading-relaxed">{article.summary}</p>
                                </div>
                            </Reveal>
                        ) : null}
                    </section>

                    {/* COVER */}
                    {article.cover_image ? (
                        <section className="container-x pb-16">
                            <Reveal>
                                <div
                                    className="w-full aspect-[16/9] bg-cover bg-center border border-brand-primary/10"
                                    style={{ backgroundImage: `url(${article.cover_image})` }}
                                />
                            </Reveal>
                        </section>
                    ) : null}

                    {/* BODY */}
                    <section className="container-x pb-20">
                        <article className="max-w-3xl mx-auto">{renderBody(article.body)}</article>
                    </section>

                    {/* FAQ */}
                    {article.faqs && article.faqs.length > 0 ? (
                        <section className="border-t border-brand-primary/10 py-20 bg-brand-surface/30">
                            <div className="container-x max-w-3xl mx-auto">
                                <p className="overline mb-6">Frequently asked</p>
                                <h2 className="font-display h-display-md text-brand-mist mb-12">
                                    Questions traders &amp; teams ask.
                                </h2>
                                <div className="divide-y divide-brand-primary/10 border-y border-brand-primary/10">
                                    {article.faqs.map((f, i) => (
                                        <details key={f.question || `faq-${i}`} className="group py-6" data-testid={`blog-faq-${i}`}>
                                            <summary className="flex items-center justify-between cursor-pointer list-none">
                                                <h3 className="font-display text-lg lg:text-xl text-brand-mist pr-6">
                                                    {f.question}
                                                </h3>
                                                <span className="shrink-0 w-6 h-6 border border-brand-primary/40 flex items-center justify-center text-brand-primary text-sm transition-transform group-open:rotate-45">
                                                    +
                                                </span>
                                            </summary>
                                            <p className="mt-4 text-brand-frost/85 leading-relaxed">{f.answer}</p>
                                        </details>
                                    ))}
                                </div>
                            </div>
                        </section>
                    ) : null}

                    {/* RELATED */}
                    {article.related && article.related.length > 0 ? (
                        <section className="border-t border-brand-primary/10 py-20">
                            <div className="container-x">
                                <p className="overline mb-6">Related reading</p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {article.related.map((r) => (
                                        <Link
                                            key={r.slug}
                                            to={`/blog/${r.slug}`}
                                            data-testid={`blog-related-${r.slug}`}
                                            className="group flex flex-col border border-brand-primary/10 bg-brand-surface hover:border-brand-primary/40 transition-colors duration-500"
                                        >
                                            <div
                                                className="aspect-[16/10] w-full bg-cover bg-center"
                                                style={{ backgroundImage: `url(${r.cover_image})` }}
                                            />
                                            <div className="p-6 flex flex-col gap-3 flex-1">
                                                <span className="text-[10px] uppercase tracking-[0.2em] text-brand-primary">
                                                    {r.category}
                                                </span>
                                                <h3 className="font-display text-lg lg:text-xl text-brand-mist transition-colors group-hover:text-brand-primary leading-snug">
                                                    {r.title}
                                                </h3>
                                                <span className="mt-auto inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-brand-primary/70">
                                                    Read
                                                    <ArrowUpRight className="w-3.5 h-3.5" />
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </section>
                    ) : null}

                    {/* FOOTER NAV */}
                    <section className="py-20 border-t border-brand-primary/10">
                        <div className="container-x flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                            <Link
                                to="/blog"
                                className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-brand-primary hover:text-brand-mist transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                                Back to blog
                            </Link>
                            <Link to="/contact" className="btn-secondary">
                                Work with us <ArrowUpRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </section>
                </>
            ) : null}
        </div>
    );
}
